import { useCallback, useMemo, useState, type ChangeEvent } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolInvoke } from '@saas-pages/shared/public/runtime'
import { percentageCalculatorRoute } from '@/shared/utility-registry'

const UTILITY = percentageCalculatorRoute.analyticsKey

type CalculatorMode = 'percent-of' | 'what-percent' | 'increase-decrease'
type AdjustmentDirection = 'increase' | 'decrease'
type PercentageError = 'invalid_percent' | 'invalid_value' | 'invalid_total' | 'zero_total'

interface CalculationResult {
  label: string
  value: number
  formula: string
  details: string[]
}

interface CalculationState {
  result: CalculationResult | null
  error: PercentageError | null
}

interface ModeOption {
  key: CalculatorMode
  title: string
  description: string
}

const modes: ModeOption[] = [
  {
    key: 'percent-of',
    title: 'X% от Y',
    description: 'Например: 15% от 240',
  },
  {
    key: 'what-percent',
    title: 'X от Y в процентах',
    description: 'Например: 18 от 240',
  },
  {
    key: 'increase-decrease',
    title: 'Изменить на %',
    description: 'Увеличить или уменьшить число',
  },
]

function parseNumber(value: string): number | null {
  const normalized = value.trim().replace(/\s+/g, '').replace(',', '.')
  if (normalized === '') return null

  const parsed = Number(normalized)
  if (!Number.isFinite(parsed)) return null

  return parsed
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('ru-RU', {
    maximumFractionDigits: 10,
  }).format(value)
}

function inputHandler(setValue: (value: string) => void) {
  return (event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value)
  }
}

function calculatePercentage(
  mode: CalculatorMode,
  direction: AdjustmentDirection,
  percent: string,
  value: string,
  total: string,
): CalculationState {
  const parsedPercent = parseNumber(percent)
  const parsedValue = parseNumber(value)
  const parsedTotal = parseNumber(total)

  if (mode === 'percent-of') {
    if (percent.trim() === '' || value.trim() === '') return { result: null, error: null }
    if (parsedPercent === null) return { result: null, error: 'invalid_percent' }
    if (parsedValue === null) return { result: null, error: 'invalid_value' }

    return {
      error: null,
      result: {
        label: `${formatNumber(parsedPercent)}% от ${formatNumber(parsedValue)}`,
        value: (parsedPercent / 100) * parsedValue,
        formula: `${formatNumber(parsedValue)} × ${formatNumber(parsedPercent)} / 100`,
        details: [
          `Исходное число: ${formatNumber(parsedValue)}`,
          `Процент: ${formatNumber(parsedPercent)}%`,
        ],
      },
    }
  }

  if (mode === 'what-percent') {
    if (value.trim() === '' || total.trim() === '') return { result: null, error: null }
    if (parsedValue === null) return { result: null, error: 'invalid_value' }
    if (parsedTotal === null) return { result: null, error: 'invalid_total' }
    if (parsedTotal === 0) return { result: null, error: 'zero_total' }

    return {
      error: null,
      result: {
        label: `${formatNumber(parsedValue)} от ${formatNumber(parsedTotal)}`,
        value: (parsedValue / parsedTotal) * 100,
        formula: `${formatNumber(parsedValue)} / ${formatNumber(parsedTotal)} × 100`,
        details: [
          `Часть: ${formatNumber(parsedValue)}`,
          `Общее число: ${formatNumber(parsedTotal)}`,
        ],
      },
    }
  }

  if (percent.trim() === '' || value.trim() === '') return { result: null, error: null }
  if (parsedPercent === null) return { result: null, error: 'invalid_percent' }
  if (parsedValue === null) return { result: null, error: 'invalid_value' }

  const delta = (parsedPercent / 100) * parsedValue
  const resultValue = direction === 'increase' ? parsedValue + delta : parsedValue - delta

  return {
    error: null,
    result: {
      label: direction === 'increase'
        ? `${formatNumber(parsedValue)} + ${formatNumber(parsedPercent)}%`
        : `${formatNumber(parsedValue)} - ${formatNumber(parsedPercent)}%`,
      value: resultValue,
      formula: direction === 'increase'
        ? `${formatNumber(parsedValue)} + (${formatNumber(parsedValue)} × ${formatNumber(parsedPercent)} / 100)`
        : `${formatNumber(parsedValue)} - (${formatNumber(parsedValue)} × ${formatNumber(parsedPercent)} / 100)`,
      details: [
        `Исходное число: ${formatNumber(parsedValue)}`,
        `Изменение: ${formatNumber(delta)}`,
      ],
    },
  }
}

export function PercentageCalculatorPage() {
  const [mode, setMode] = useState<CalculatorMode>('percent-of')
  const [percent, setPercent] = useState('15')
  const [value, setValue] = useState('240')
  const [total, setTotal] = useState('240')
  const [direction, setDirection] = useState<AdjustmentDirection>('increase')

  const activeMode = useMemo(
    () => modes.find((item) => item.key === mode) ?? modes[0]!,
    [mode],
  )

  const { result, error } = useMemo(
    () => calculatePercentage(mode, direction, percent, value, total),
    [direction, mode, percent, total, value],
  )

  const handleMode = useCallback((nextMode: CalculatorMode) => {
    setMode(nextMode)
    toolInvoke(UTILITY, { action: 'mode', mode: nextMode })
  }, [])

  const handleDirection = useCallback((nextDirection: AdjustmentDirection) => {
    setDirection(nextDirection)
    toolInvoke(UTILITY, { action: 'direction', direction: nextDirection })
  }, [])

  const reset = useCallback(() => {
    setPercent('15')
    setValue('240')
    setTotal('240')
    setDirection('increase')
    toolInvoke(UTILITY, { action: 'reset' })
  }, [])

  return (
    <div className="mx-auto max-w-6xl">
      <section className="overflow-hidden rounded-lg border bg-card text-card-foreground shadow">
        <div
          aria-hidden="true"
          className="h-1.5 bg-gradient-to-r from-teal-600 via-amber-400 to-red-500"
        />
        <div className="grid gap-0 lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.86fr)]">
          <div className="space-y-3 border-b p-4 sm:space-y-5 sm:p-6 lg:border-b-0 lg:border-r">
            <div className="grid grid-cols-3 gap-1 sm:gap-2" role="tablist" aria-label="Режим расчёта">
              {modes.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  role="tab"
                  aria-selected={mode === item.key}
                  onClick={() => handleMode(item.key)}
                  className={cn(
                    'min-h-11 min-w-0 rounded-md border px-1.5 py-1.5 text-center text-sm leading-tight transition-colors sm:p-3 sm:text-left',
                    mode === item.key
                      ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                      : 'bg-background hover:border-primary/40 hover:bg-accent/60',
                  )}
                >
                  <span className="block font-semibold">{item.title}</span>
                  <span className={cn(
                    'mt-1 hidden text-xs leading-5 sm:block',
                    mode === item.key ? 'text-primary-foreground/80' : 'text-muted-foreground',
                  )}
                  >
                    {item.description}
                  </span>
                </button>
              ))}
            </div>

            <div className="sm:rounded-md sm:border sm:bg-background/70 sm:p-4 sm:shadow-inner">
              <div className="mb-4 hidden flex-wrap items-center justify-between gap-2 sm:flex">
                <h2 className="text-base font-semibold tracking-normal">{activeMode.title}</h2>
                <span className="rounded-md border bg-card px-2 py-1 text-xs text-muted-foreground">
                  {activeMode.description}
                </span>
              </div>

              {mode === 'what-percent' ? (
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <NumberField
                    id="value"
                    label="Число X"
                    value={value}
                    placeholder="18"
                    onChange={inputHandler(setValue)}
                  />
                  <NumberField
                    id="total"
                    label="От числа Y"
                    value={total}
                    placeholder="240"
                    onChange={inputHandler(setTotal)}
                  />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <NumberField
                    id="percent"
                    label="Процент"
                    value={percent}
                    placeholder="15"
                    suffix="%"
                    onChange={inputHandler(setPercent)}
                  />
                  <NumberField
                    id="value"
                    label="Число"
                    value={value}
                    placeholder="240"
                    onChange={inputHandler(setValue)}
                  />
                </div>
              )}

              {mode === 'increase-decrease' && (
                <div className="mt-4 inline-flex rounded-md border bg-card p-1">
                  <button
                    type="button"
                    onClick={() => handleDirection('increase')}
                    className={cn(
                      'rounded px-3 py-1.5 text-sm font-medium transition-colors',
                      direction === 'increase'
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                    )}
                  >
                    Увеличить
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDirection('decrease')}
                    className={cn(
                      'rounded px-3 py-1.5 text-sm font-medium transition-colors',
                      direction === 'decrease'
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                    )}
                  >
                    Уменьшить
                  </button>
                </div>
              )}

              {error && (
                <div className="mt-4 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {errorText(error)}
                </div>
              )}
            </div>
          </div>

          <ResultPanel result={result} onReset={reset} />
        </div>
      </section>

      <section className="mt-8 grid gap-4 md:grid-cols-3">
        <InfoCard title="Без регистрации" text="Откройте страницу и сразу считайте проценты." />
        <InfoCard title="Локальная обработка" text="Введённые числа остаются в вашем браузере." />
        <InfoCard title="Понятная формула" text="Калькулятор показывает итог и шаг расчёта." />
      </section>
    </div>
  )
}

interface NumberFieldProps {
  id: string
  label: string
  value: string
  placeholder: string
  suffix?: string
  onChange: (event: ChangeEvent<HTMLInputElement>) => void
}

function NumberField({ id, label, value, placeholder, suffix, onChange }: NumberFieldProps) {
  return (
    <label htmlFor={id} className="block">
      <span className="mb-2 block text-sm font-medium">{label}</span>
      <span className="relative block">
        <input
          id={id}
          inputMode="decimal"
          value={value}
          placeholder={placeholder}
          onChange={onChange}
          className={cn(
            'h-11 w-full rounded-md border bg-card px-3 text-base outline-none transition-colors',
            'focus:border-primary focus:ring-2 focus:ring-ring/20',
            suffix && 'pr-9',
          )}
        />
        {suffix && (
          <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground">
            {suffix}
          </span>
        )}
      </span>
    </label>
  )
}

function ResultPanel({ result, onReset }: { result: CalculationResult | null; onReset: () => void }) {
  return (
    <div className="flex flex-col justify-between bg-background/55 p-4 sm:p-6 lg:min-h-[360px]">
      <div>
        <div className="mb-2 text-sm font-medium text-muted-foreground sm:mb-4">Результат</div>
        {result ? (
          <div>
            <div className="text-sm text-muted-foreground">{result.label}</div>
            <div className="mt-2 break-words font-mono text-4xl font-semibold tabular-nums text-foreground sm:text-5xl">
              {formatNumber(result.value)}
              {result.label.includes(' от ') && !result.label.includes('% от') ? '%' : ''}
            </div>
            <div className="mt-5 rounded-md border bg-card p-4 shadow-sm">
              <div className="text-xs font-medium uppercase text-muted-foreground">Формула</div>
              <div className="mt-2 break-words font-mono text-sm">{result.formula}</div>
            </div>
            <div className="mt-4 grid gap-2">
              {result.details.map((detail) => (
                <div key={detail} className="rounded-md border bg-card px-3 py-2 text-sm text-muted-foreground">
                  {detail}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-sm leading-6 text-muted-foreground">
            Введите числа: результат и формула обновятся сразу.
          </p>
        )}
      </div>

      <div className="mt-4">
        <Button variant="outline" onClick={onReset}>Сбросить</Button>
      </div>
    </div>
  )
}

function InfoCard({ title, text }: { title: string; text: string }) {
  return (
    <Card className="rounded-lg">
      <CardContent className="p-4">
        <h2 className="font-semibold tracking-normal">{title}</h2>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">{text}</p>
      </CardContent>
    </Card>
  )
}

function errorText(error: PercentageError): string {
  if (error === 'invalid_percent') return 'Введите корректный процент.'
  if (error === 'invalid_value') return 'Введите корректное число.'
  if (error === 'invalid_total') return 'Введите корректное общее число.'
  return 'Общее число не должно быть равно нулю.'
}
